/**
 * The two "Pages you like" slots on Design the page. A filled slot is a small
 * browser window that glides through the whole screenshot on hover, with the
 * six lines read from it right underneath.
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties, type DragEvent } from "react";
import type { PageInspirations, PageSlot, StructureBrief } from "../../../shared/types";
import { Icon } from "../../components/ui/Icon";
import { cx } from "../../components/ui/cx";
import { openAssets } from "../../assets/store";
import { downscale, firstFree, SLOTS, SLOT_LETTER } from "../inspirations";
import { glideSeconds, glideTravel } from "./glide";
import { ShotLines } from "./ShotLines";
import { ShotViewer } from "./ShotViewer";
import type { Reads } from "./useShotReads";

const STILL = "(prefers-reduced-motion: reduce)";

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => typeof window.matchMedia === "function" && window.matchMedia(STILL).matches);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const q = window.matchMedia(STILL);
    const on = () => setReduced(q.matches);
    q.addEventListener("change", on);
    return () => q.removeEventListener("change", on);
  }, []);
  return reduced;
}

/** The picture in its window: it glides to the foot of the page on hover and back on leave, or scrolls under the wheel for who asks for less motion. */
function ShotWindow({ url, onOpen }: { url: string; onOpen: () => void }) {
  const win = useRef<HTMLDivElement>(null);
  const [travel, setTravel] = useState(0);
  const [go, setGo] = useState(false);
  const still = useReducedMotion();

  const measure = useCallback(() => {
    const el = win.current;
    const img = el?.querySelector("img");
    if (el && img) setTravel(glideTravel(img.offsetHeight, el.clientHeight));
  }, []);
  useEffect(() => {
    const el = win.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  const style = { "--glide-y": `-${travel}px`, "--glide-s": `${glideSeconds(travel)}s` } as CSSProperties;
  return (
    <div
      className="nbshot-stage"
      onMouseEnter={() => setGo(true)}
      onMouseLeave={() => setGo(false)}
      onFocus={() => setGo(true)}
      onBlur={() => setGo(false)}
    >
      {/* The button below is the keyboard's way in; a click on the picture itself opens the same viewer. */}
      <div ref={win} className={cx("nbshot-win", still && "still", go && !still && "go")} style={style} onClick={onOpen}>
        <img className="nbshot-img" src={url} alt="" onLoad={measure} />
      </div>
      <span className="nbshot-fade" aria-hidden="true" />
      <button type="button" className="btn s nbshot-view" onClick={onOpen}>
        <Icon name="expand" /> View whole page
      </button>
    </div>
  );
}

export function ShotSlots({
  value,
  reads,
  onChange,
  onEditLine,
}: {
  value: PageInspirations;
  reads: Reads;
  onChange: (next: PageInspirations) => void;
  onEditLine: (slot: PageSlot, line: keyof StructureBrief, text: string) => void;
}) {
  const [over, setOver] = useState<PageSlot | null>(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState<PageSlot | null>(null);
  const live = useRef(value);
  live.current = value;

  /** Assets, narrowed to pictures; a picture dropped or pasted goes up in it, smaller, and fills the slot. */
  const fill = useCallback(
    (slot: PageSlot, incoming?: File[]) =>
      openAssets({
        fits: "image",
        title: `A page you like (${SLOT_LETTER[slot]})`,
        cta: "Use this picture",
        incoming,
        onPick: (a) => {
          if (!a.key || !a.url) return;
          // The same picture again is the one already read, so it keeps its lines.
          if (live.current[slot]?.key === a.key) return;
          onChange({ ...live.current, [slot]: { key: a.key, url: a.url, name: a.name } });
        },
      }),
    [onChange],
  );
  const take = useCallback(
    async (slot: PageSlot, file: File | null | undefined) => {
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        setError("Drop a picture of the page.");
        return;
      }
      setError("");
      fill(slot, [await downscale(file)]);
    },
    [fill],
  );

  // A picture pasted anywhere on the step fills the first free slot, A before B.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      // Assets open over the step takes what is pasted for itself.
      if (document.querySelector(".as-sheet")) return;
      const slot = firstFree(live.current);
      const pic = [...(e.clipboardData?.files ?? [])].find((f) => f.type.startsWith("image/"));
      if (!slot || !pic) return;
      e.preventDefault();
      void take(slot, pic);
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [take]);

  const opened = open ? value[open] : undefined;
  const openedRead = open ? reads[open] : undefined;
  return (
    <div className="field">
      <label>
        Pages you like <span className="muted b6">Optional</span>
      </label>
      <div className="nbshots">
        {SLOTS.map((slot) => {
          const one = value[slot];
          const L = SLOT_LETTER[slot];
          const read = one && reads[slot]?.key === one.key ? reads[slot]?.state : undefined;
          const drop = (e: DragEvent) => {
            e.preventDefault();
            setOver(null);
            void take(slot, e.dataTransfer?.files?.[0]);
          };
          if (one) {
            return (
              <div className="nbslot" key={slot}>
                <div className="nbshot on">
                  <span className="nbshot-l">{L}</span>
                  <div className="nbshot-bar" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </div>
                  <ShotWindow url={one.url} onOpen={() => setOpen(slot)} />
                  <div className="nbshot-f">
                    <span className="sp">{one.name}</span>
                    <button type="button" className="link" onClick={() => fill(slot)}>
                      Replace
                    </button>
                    <button
                      type="button"
                      className="ibtn sm"
                      aria-label={`Take picture ${L} off`}
                      onClick={() => {
                        const next = { ...value };
                        delete next[slot];
                        onChange(next);
                      }}
                    >
                      <Icon name="x" />
                    </button>
                  </div>
                </div>
                <ShotLines letter={L} brief={one.brief} state={read} onEdit={(line, text) => onEditLine(slot, line, text)} />
              </div>
            );
          }
          return (
            <div className="nbslot" key={slot}>
              <button
                type="button"
                className={cx("nbshot", over === slot && "over")}
                aria-label={`Picture for design ${L}`}
                onClick={() => fill(slot)}
                onDragOver={(e) => {
                  e.preventDefault();
                  setOver(slot);
                }}
                onDragLeave={() => setOver(null)}
                onDrop={drop}
              >
                <span className="nbshot-l">{L}</span>
                <Icon name="pic" />
                <b>Drop a screenshot</b>
                <small>
                  or paste one with <kbd>Ctrl</kbd> <kbd>V</kbd>
                </small>
              </button>
            </div>
          );
        })}
      </div>
      {error ? (
        <p className="f-err" role="alert">
          {error}
        </p>
      ) : null}
      {open && opened ? (
        <ShotViewer
          letter={SLOT_LETTER[open]}
          one={opened}
          state={openedRead?.key === opened.key ? openedRead.state : undefined}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </div>
  );
}
